import React, { useEffect, useState, useMemo } from 'react';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';

const PALETTE = [
    { bg: 'bg-rose-500', hex: '#f43f5e', light: 'bg-rose-50', text: 'text-rose-600' },
    { bg: 'bg-orange-500', hex: '#f97316', light: 'bg-orange-50', text: 'text-orange-600' },
    { bg: 'bg-violet-500', hex: '#8b5cf6', light: 'bg-violet-50', text: 'text-violet-600' },
    { bg: 'bg-sky-500', hex: '#0ea5e9', light: 'bg-sky-50', text: 'text-sky-600' },
    { bg: 'bg-emerald-500', hex: '#10b981', light: 'bg-emerald-50', text: 'text-emerald-600' },
    { bg: 'bg-pink-500', hex: '#ec4899', light: 'bg-pink-50', text: 'text-pink-600' },
];
const getColor = (i) => PALETTE[i % PALETTE.length];

const applyPeriod = (list, period) => {
    if (period === 'all') return list;
    const cutoff = new Date();
    if (period === 'today') cutoff.setHours(0, 0, 0, 0);
    else if (period === '7d') cutoff.setDate(cutoff.getDate() - 7);
    else cutoff.setDate(cutoff.getDate() - 30);
    return list.filter(e => new Date(e.timestamp) >= cutoff);
};

const fmt = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

const ErrorDetails = () => {
    const navigate = useNavigate();
    const [allErrors, setAllErrors] = useState([]);
    const [tenantCount, setTenantCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [period, setPeriod] = useState('30d');
    const [expandedMode, setExpandedMode] = useState(null);
    const [logSearch, setLogSearch] = useState('');

    useEffect(() => {
        const fetchErrors = async () => {
            try {
                setLoading(true);
                const res = await api.get('/api/errors/all');
                setAllErrors(res.data.errors || []);
                setTenantCount(res.data.tenantCount ?? null);
            } catch (err) {
                setFetchError(err.response?.data?.message || 'Impossible de charger les données.');
            } finally {
                setLoading(false);
            }
        };
        fetchErrors();
    }, []);

    const errors = useMemo(() => applyPeriod(allErrors, period), [allErrors, period]);

    const modeStats = useMemo(() => {
        const grouped = errors.reduce((acc, e) => {
            const key = e.mode || 'unknown';
            if (!acc[key]) acc[key] = [];
            acc[key].push(e);
            return acc;
        }, {});
        return Object.entries(grouped)
            .sort((a, b) => b[1].length - a[1].length)
            .map(([mode, recs]) => {
                const freq = recs.reduce((acc, e) => {
                    const msg = e.error_message || 'Unknown';
                    acc[msg] = (acc[msg] || 0) + 1;
                    return acc;
                }, {});
                const errorTypes = Object.entries(freq)
                    .sort((a, b) => b[1] - a[1])
                    .map(([msg, count]) => ({
                        type: msg, count,
                        percentage: Math.round((count / recs.length) * 100),
                    }));
                return { mode, totalErrors: recs.length, errorTypes, records: recs };
            });
    }, [errors]);

    const mostCommon = useMemo(() => {
        const freq = errors.reduce((acc, e) => {
            const msg = e.error_message || 'Unknown';
            acc[msg] = (acc[msg] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(freq).sort((a, b) => b[1] - a[1])[0];
    }, [errors]);

    const activeLogs = useMemo(() => {
        const ms = modeStats.find(m => m.mode === expandedMode);
        if (!ms) return [];
        const q = logSearch.toLowerCase();
        if (!q) return ms.records;
        return ms.records.filter(r =>
            r.error_message?.toLowerCase().includes(q) ||
            r.user_id?.toLowerCase().includes(q) ||
            r.message_user?.toLowerCase().includes(q) ||
            r.session_id?.toLowerCase().includes(q)
        );
    }, [expandedMode, modeStats, logSearch]);

    return (
        <div className="flex h-screen bg-gray-50">
            <Sidebar />
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
                    {/* Header */}
                    <div className="flex justify-between items-center flex-wrap gap-4">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/analytics')}
                                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                            >
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                            </button>
                            <h1 className="text-2xl font-bold text-gray-900">Typologie des erreurs par mode</h1>
                        </div>
                        {/* Period filter */}
                        <div className="flex bg-white rounded-lg shadow-sm border border-gray-100 p-1">
                            {[
                                { label: "Aujourd'hui", value: 'today' },
                                { label: '7 jours', value: '7d' },
                                { label: '30 jours', value: '30d' },
                                { label: 'Tout', value: 'all' },
                            ].map(p => (
                                <button
                                    key={p.value}
                                    onClick={() => setPeriod(p.value)}
                                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                        period === p.value ? 'bg-red-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Loading / Error states */}
                    {loading && (
                        <div className="flex items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500"></div>
                            <span className="ml-3 text-gray-500">Chargement des données...</span>
                        </div>
                    )}

                    {fetchError && !loading && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                            {fetchError}
                        </div>
                    )}

                    {!loading && !fetchError && (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <h3 className="text-sm font-medium text-gray-500">Total Errors</h3>
                                    <p className="text-3xl font-bold text-red-600 mt-2">{errors.length}</p>
                                </div>
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <h3 className="text-sm font-medium text-gray-500">Modes avec erreurs</h3>
                                    <p className="text-3xl font-bold text-gray-900 mt-2">{modeStats.length}</p>
                                </div>
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <h3 className="text-sm font-medium text-gray-500">Erreur la plus fréquente</h3>
                                    <p className="text-sm font-bold text-gray-900 mt-2 truncate" title={mostCommon?.[0]}>
                                        {mostCommon ? `${mostCommon[0]} (×${mostCommon[1]})` : '—'}
                                    </p>
                                </div>
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <h3 className="text-sm font-medium text-gray-500">Tenants enregistrés</h3>
                                    <p className="text-3xl font-bold text-indigo-600 mt-2">{tenantCount ?? '—'}</p>
                                </div>
                            </div>

                            {/* Error Details by Mode */}
                            <div className="space-y-6">
                                {modeStats.map(({ mode, totalErrors, errorTypes, records }) => (
                                    <div key={mode} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                        <div className="flex justify-between items-center mb-6">
                                            <div>
                                                <h2 className="text-lg font-bold text-gray-900 capitalize">{mode}</h2>
                                                <p className="text-sm text-gray-500">{totalErrors} erreur{totalErrors > 1 ? 's' : ''}</p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="text-right">
                                                    <p className="text-2xl font-bold text-red-600">{totalErrors}</p>
                                                    <p className="text-sm text-gray-500">Total</p>
                                                </div>
                                                <button
                                                    onClick={() => setExpandedMode(expandedMode === mode ? null : mode)}
                                                    className="text-sm px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 transition-colors"
                                                >
                                                    {expandedMode === mode ? 'Masquer les logs' : 'Voir les logs'}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Distribution Bar */}
                                        <div className="mb-4">
                                            <div className="flex h-4 rounded-full overflow-hidden">
                                                {errorTypes.map((e, index) => (
                                                    <div
                                                        key={e.type}
                                                        className={`${getColor(index).bg} transition-all duration-300`}
                                                        style={{ width: `${e.percentage}%` }}
                                                        title={`${e.type}: ${e.count} (${e.percentage}%)`}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        {/* Error Message Frequency Table */}
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full">
                                                <thead>
                                                    <tr className="border-b border-gray-200">
                                                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Message d'erreur</th>
                                                        <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Occurrences</th>
                                                        <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">%</th>
                                                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase w-1/4">Distribution</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {errorTypes.map((e, index) => (
                                                        <tr key={e.type} className="border-b border-gray-100 hover:bg-gray-50">
                                                            <td className="py-3 px-4">
                                                                <div className="flex items-center gap-2">
                                                                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${getColor(index).bg}`}></div>
                                                                    <span className="text-sm font-medium text-gray-900 break-all">{e.type}</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-3 px-4 text-right text-sm text-gray-600">{e.count}</td>
                                                            <td className="py-3 px-4 text-right text-sm text-gray-600">{e.percentage}%</td>
                                                            <td className="py-3 px-4">
                                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                                    <div
                                                                        className={`h-2 rounded-full ${getColor(index).bg}`}
                                                                        style={{ width: `${e.percentage}%` }}
                                                                    ></div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Expanded Raw Logs */}
                                        {expandedMode === mode && (
                                            <div className="mt-6 border-t border-gray-100 pt-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h3 className="text-sm font-semibold text-gray-700">Logs détaillés ({activeLogs.length})</h3>
                                                    <input
                                                        type="text"
                                                        value={logSearch}
                                                        onChange={e => setLogSearch(e.target.value)}
                                                        placeholder="Rechercher..."
                                                        className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-red-400 w-48"
                                                    />
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full text-xs">
                                                        <thead>
                                                            <tr className="border-b border-gray-200">
                                                                <th className="text-left py-2 px-3 font-medium text-gray-500 uppercase">Timestamp</th>
                                                                <th className="text-left py-2 px-3 font-medium text-gray-500 uppercase">Tenant</th>
                                                                <th className="text-left py-2 px-3 font-medium text-gray-500 uppercase">User ID</th>
                                                                <th className="text-left py-2 px-3 font-medium text-gray-500 uppercase">Session ID</th>
                                                                <th className="text-left py-2 px-3 font-medium text-gray-500 uppercase">Type</th>
                                                                <th className="text-left py-2 px-3 font-medium text-gray-500 uppercase">Message utilisateur</th>
                                                                <th className="text-left py-2 px-3 font-medium text-gray-500 uppercase">Erreur</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {activeLogs.map((rec) => (
                                                                <tr key={rec.id} className="border-b border-gray-50 hover:bg-gray-50">
                                                                    <td className="py-2 px-3 text-gray-500 whitespace-nowrap">{fmt(rec.timestamp)}</td>
                                                                    <td className="py-2 px-3 whitespace-nowrap">
                                                                        {rec.tenant ? (
                                                                            <div>
                                                                                <p className="text-xs font-semibold text-indigo-700">#{rec.tenant.tenant_id}</p>
                                                                                <p className="text-xs text-gray-500 truncate max-w-[120px]" title={rec.tenant.company_name}>{rec.tenant.company_name}</p>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-xs text-gray-300">—</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="py-2 px-3 text-gray-700 whitespace-nowrap">{rec.user_id}</td>
                                                                    <td className="py-2 px-3 text-gray-500 whitespace-nowrap">{rec.session_id}</td>
                                                                    <td className="py-2 px-3">
                                                                        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">{rec.type}</span>
                                                                    </td>
                                                                    <td className="py-2 px-3 text-gray-700 max-w-xs truncate" title={rec.message_user}>{rec.message_user}</td>
                                                                    <td className="py-2 px-3 text-red-600 max-w-xs truncate" title={rec.error_message}>{rec.error_message}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {modeStats.length === 0 && (
                                    <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-100 text-center text-gray-400">
                                        Aucune erreur trouvée dans la base de données.
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ErrorDetails;
